import { CellType, ICallToActionProps } from '@amobee/component-library';
import { createReactTemplateElement } from '@angular-react/core';
import { Component, NgZone, OnInit, TemplateRef, ViewChild, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CampaignAdapter } from 'src/adapters/campaign-adapter';
import { AutoCompleteMappedItem } from 'src/app/common/components/auto-complete/auto-complete.component';
import { TableConfigs } from 'src/app/common/components/data-table/data-table.component';
import { ListResponse } from 'src/app/data/interfaces/ListResponse';
import { addRemoveInvisibleScroll, applySearchAndPagination, doFilter } from 'src/app/misc/util/util';
import { BreadcrumbService } from 'src/app/services/breadcrumb.service';

@Component({
  selector: 'company-list',
  templateUrl: './company-list.component.html',
  styleUrls: ['./company-list.component.scss']
})
export class CompanyListComponent implements OnInit, OnDestroy {

  @ViewChild('compListBrandCountTemplate', { static: true }) public compListBrandCountTemplate: TemplateRef<any>;
  @ViewChild('compListCampaignCountTemplate', { static: true }) public compListCampaignCountTemplate: TemplateRef<any>;

  showContentLoading: boolean = false;
  showBrandSheet: boolean = false;
  showCampaignSheet: boolean = false;
  brandDetail: any;
  campaignDetail: any;
  customerList: any[] = [];
  isDataAlreadyFetched: boolean = false;
  fullDataList: Array<any> = [];

  constructor(
    private campaignAdapter: CampaignAdapter,
    private router: Router,
    private route: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    public ngZone: NgZone
  ) { }

  ngOnInit() {
    addRemoveInvisibleScroll(true);
    this.campaignAdapter.getCustomers().then((customers) => {
      if (customers) {
        this.customerList = customers.map(v => {
          return {
            label: v.Name,
            value: v.Id
          }
        });
      }

    });
    this.breadcrumbService.prepareBreadcrumb();
  }

  public firstTimeUserCallToAction: ICallToActionProps = {
    type: 'EmptyTable',
    title: 'No Results Found'
  };

  public tableConfigs: TableConfigs = {
    disableSorting: false,
    defaultSort: {
      id: 'name',
      desc: true
    },
    uniqueIdAccessor: 'id',
    pageSize: 25, // default to 25
    isSelectionEnabled: false,
    isPaginationEnabled: true,
    showSummaryRow: false,
    stickyHeader: true,
    stickyColumnHeaders: true,
    enableStickyColumns: true,
    fetchStaticData: (params: any) => {
      this.showContentLoading = true;
      if (this.isDataAlreadyFetched) {
        this.showContentLoading = false;
        return Promise.resolve(doFilter(this.fullDataList, params))
      } else {
        return this.campaignAdapter.getCustomersListData().then((value: ListResponse) => {
          this.fullDataList = value.results;
          this.isDataAlreadyFetched = true;
          this.showContentLoading = false;
          return doFilter(this.fullDataList, params);
        });
      }
    },
    showCallToAction: true,
    callToActionProps: this.firstTimeUserCallToAction
  };

  public columns: any[] = [
    {
      Header: 'Company',
      accessor: 'name',
      id: 'name',
      cellType: CellType.ROW_ACTION,
      onLinkClick: row => {
        this.showContentLoading = true;
        this.campaignAdapter.updateCBAId(row.original.customerId, null, null);
        this.router.navigate(['..', 'advertiser-list'], { relativeTo: this.route }).catch(() => {
          this.showContentLoading = false;
        });
      },
      isActive: () => true
    },
    {
      Header: 'Advertiser',
      accessor: 'advertiserName',
      id: 'advertiserName',
      cellType: CellType.ROW_ACTION,
      onLinkClick: row => {
        this.showContentLoading = true;
        this.campaignAdapter.updateCBAId(row.original.customerId, row.original.id, null);
        this.router.navigate(['advertiser/campaign-list']).catch(() => {
          this.showContentLoading = false;
        });
      },
      isActive: () => true
    },
    {
      Header: 'Brands',
      accessor: 'brands',
      id: 'brand',
      Cell: props => {
        return createReactTemplateElement(this.compListBrandCountTemplate, props.row.original, this.ngZone);
      },
      disableSortBy: true
    },
    {
      Header: 'Campaigns',
      accessor: 'campaigns',
      id: 'campaign',
      classNames: 'test',
      Cell: props => {
        return createReactTemplateElement(this.compListCampaignCountTemplate, props.row.original, this.ngZone);
      },
      disableSortBy: true
    }
  ];

  public autoCompletetConfig = {
    companyAutoCompletePromise: (params: any) => {
      return Promise.resolve(applySearchAndPagination(this.customerList, params));
    },
    handleOnCompanySelect: (item: AutoCompleteMappedItem) => {
      this.showContentLoading = true;
      this.campaignAdapter.updateCBAId(+item.value, null, null);
      this.router.navigate(['..', 'advertiser-list'], { relativeTo: this.route }).catch(() => {
        this.showContentLoading = false;
      });
    },
    responseMapper: (response: any, offset: number, limit: number) => {
      return {
        results: response,
        hasMore: response.length >= limit
      }
    }
  };

  openBrandSheet(name, advertiserName, customerId, id, brands) {
    if (brands > 0) {
      this.showBrandSheet = true;
      this.brandDetail = {
        header: name + " > " + advertiserName,
        customerId: customerId,
        advertiserId: id,
        count: brands
      }
    }
  }

  openCampaignSheet(name, advertiserName, customerId, id, campaigns) {
    if (campaigns > 0) {
      this.showCampaignSheet = true;
      this.campaignDetail = {
        header: name + " > " + advertiserName,
        customerId: customerId,
        advertiserId: id,
        count: campaigns
      }
    }
  }

  closeSidebar() {
    this.showBrandSheet = false;
    this.showCampaignSheet = false;
    this.campaignDetail = null;
    this.brandDetail = null;
  }

  ngOnDestroy(): void {
    addRemoveInvisibleScroll(false);
  }

}
